import {
  Component,
  Element,
  Event,
  EventEmitter,
  Fragment,
  h,
  Host,
  Listen,
  Method,
  Prop,
  readTask,
  State,
  Watch,
  writeTask,
} from "@stencil/core";

import { hasSlot } from "../../utils/utils";
import { media } from "../../utils/media-utils";
import {
  StzhAppNavItem,
  StzhDropdownOption,
  StzhHeaderLanguageChangeEvent,
  StzhHeaderMenuItem,
  StzhHeaderMenuItemClickEvent,
  StzhHeaderMetanavItem,
  StzhHeaderMetanavItemButton,
  StzhHeaderMetanavItemButtonClickEvent,
  StzhHeaderMetanavItemChild,
  StzhHeaderMetanavItemChildClickEvent,
  StzhHeaderMetanavItemClickEvent,
  StzhHeaderSearchChangedEvent,
  StzhHeaderSearchChangeEvent,
} from "../../index";

import { tabbable } from "tabbable";
import { createBaseFocusTrapOptions } from "../../utils/overlay-utils";
import { createFocusTrap, FocusTrap } from "focus-trap";

import { StzhHeaderLocalizedText } from "./stzh-header.localization";

const CLASS_BODY_OPEN = "stzh-header-open";

// for flyout a11y see also: https://www.w3.org/WAI/tutorials/menus/flyout/

/**
 * @slot logo - Slot for brand logo
 * @slot menu-before - Slot for custom elements before other menu elements
 * @slot menu-after - Slot for custom elements after other menu elements
 * @slot metanav-before - Slot for custom elements before other metanav elements
 * @slot metanav-after - Slot for custom elements after other metanav elements
 * @slot search - Slot for search element
 */
@Component({
  tag: "stzh-header",
  styleUrl: "stzh-header.scss",
  scoped: true,
})
export class StzhHeader {
  /** Translation strings */
  @Prop() localization: StzhHeaderLocalizedText;

  /** Portal link (for logo) */
  @Prop() href: string = "https://www.stadt-zuerich.ch";

  /** Menu element ID */
  @Prop() menuId: string = "anchorNavMain";

  /** Search element ID */
  @Prop() searchId: string = "anchorSearch";

  /** Metanav element ID */
  @Prop() metanavId: string = "anchorNavMeta";

  /** Language element ID */
  @Prop() languageId: string = "anchorNavLang";

  /**
   * Type of logo (used for setting the correct height).
   * Will enlarge the logo to 100% by default (default logos are using whitespace).
   */
  @Prop({ reflect: true }) logoType: "default" | "vbz" | "aoz" | "pkzh" | "uvz" = "default";

  /** Menu navigation items */
  @Prop() menuItems: StzhHeaderMenuItem[] | string = [];
  @State() _menuItems: StzhHeaderMenuItem[];

  /** Overwrite menu back label */
  @Prop() menuBackLabel: string;

  /** Generic meta navigation items */
  @Prop() metanavItems: StzhHeaderMetanavItem[] | string = [];
  @State() _metanavItems: StzhHeaderMetanavItem[];

  /** User specific meta navigation items when the user is not logged in or the api call failed */
  @Prop() userSpecificMetanavItemsNotLoggedIn: StzhHeaderMetanavItem[] = [];
  @State() _userSpecificMetanavItemsNotLoggedIn: StzhHeaderMetanavItem[] = [];

  /** App-Nav items */
  @Prop() appNavItems: StzhAppNavItem[] | string = [];
  @State() _appNavItems: StzhAppNavItem[];

  /** Endpoint for cart and user menu when logged in */
  @Prop() userSpecificMetanavItemsEndpoint: string;

  /** Current language page path */
  @Prop({ mutable: true }) languageActive: string;

  /** Available language (paths) in language switch */
  @Prop() languages: StzhDropdownOption[] | string = [];
  @State() _languages: StzhDropdownOption[];

  /** Prevent url change when language has changed (will only fire `stzhLanguageChange`) */
  @Prop() languagePreventUrlchange: boolean = false;

  /** Whether language switch should stay on mobile */
  @Prop() languageStay: boolean = false;

  /** Whether language switch should stay on mobile */
  @Prop() hideMetabar: boolean = false;

  /** Search form action (if given, searchfield will be shown) */
  @Prop() searchAction: string;

  /** Search field name */
  @Prop() searchFieldName: string = "q";

  /** Search input value */
  @Prop() searchValue: string = "";

  /** Sticky behaviour */
  @Prop() sticky: "default" | "disabled" | "always-full" | "always" = "default";

  /** Is sent to stzh-toastbar when the fetch for the user specific data fails */
  @Prop() loginErrorMessage: string = "";

  /**
   * For Adobe Analytics.
   * Will be rendered as value of `s-object-id` attribute to the logo link element.
   * Default value is "Header Logo".
   */
  @Prop() logoAnalyticsId: string;

  @State() hideLogo: boolean = false;
  @State() headerHeight: number;
  @State() paddingTop: number;
  @State() stickyActive: boolean = false;
  @State() flyoutOpen: boolean = false;
  @State() flyoutSearchOpen: boolean = false;
  @State() searchFilled: boolean;

  @State() fixed: boolean = false;
  @State() fixedTransition: boolean = false;

  @State() currentOpenMenuItem: StzhHeaderMenuItem = null;
  @State() currentOpenMetanavItem: StzhHeaderMetanavItem = null;
  @State() currentOpenLanguage: boolean = false;

  @State() userSpecificMetanavItemsLoggedIn: StzhHeaderMetanavItem[] = [];

  @State() combinedMetanavItems: StzhHeaderMetanavItem[] = [];

  private openSubmenuElement: HTMLElement;

  @Element() element: HTMLStzhHeaderElement;

  @Listen("keydown", { target: "document" })
  handleKeydown(event: KeyboardEvent) {
    if (event.key === "Escape") {
      this.flyoutOpen = false;
      this.flyoutSearchOpen = false;
    }
  }

  @Listen("stzhOverlayCloseClick")
  flyoutCloseClicked() {
    this.flyoutSearchOpen = false;
    this.updatePosition();
  }

  @Watch("flyoutOpen")
  @Watch("flyoutSearchOpen")
  async flyoutOpenWatcher(open: boolean) {
    if (!open) {
      // reset current open menu when flyout was closed
      this.currentOpenMenuItem = null;
      this.currentOpenMetanavItem = null;
      this.currentOpenLanguage = null;
      // reset further flags
      this.flyoutOpenedByMetanavItem = false;

      document.body.classList.remove(CLASS_BODY_OPEN);
      this.enableSiblings();

      if (this.trap) {
        this.trap.deactivate();
      }
    } else {
      document.body.classList.add(CLASS_BODY_OPEN);
      this.disableSiblings();

      if (this.trap) {
        this.trap.activate();
      }
    }
  }

  @Watch("fixed")
  @Watch("flyoutOpen")
  @Watch("flyoutSearchOpen")
  @Watch("headerHeight")
  async fixedWatcher() {
    this.paddingTop = this.fixed || this.flyoutOpen || this.flyoutSearchOpen ? this.headerHeight : 0;
    // we don't want to run transitions on applying fixed class (out transition),
    // so we set them in the next render
    await this.waitForNextRender();
    this.fixedTransition = this.fixed || this.flyoutOpen || this.flyoutSearchOpen;
  }

  @Watch("sticky")
  stickyWatcher() {
    this.updatePosition();
  }

  /** Update position / sticky state of header */
  @Method()
  async updatePosition() {
    if (this.flyoutOpen || this.flyoutSearchOpen) {
      return;
    }

    const activateSticky = () => {
      this.stickyActive = true;
      document.documentElement.style.setProperty("--stzh-header-is-stuck", "1");
      document.documentElement.style.setProperty("--stzh-header-is-not-stuck", "0");
    };

    const disableSticky = () => {
      document.documentElement.style.setProperty("--stzh-header-is-stuck", "0");
      document.documentElement.style.setProperty("--stzh-header-is-not-stuck", "1");
      this.stickyActive = false;
    };

    if (this.sticky === "disabled") {
      disableSticky();
    } else if (this.sticky === "always-full") {
      activateSticky();
    } else if (this.sticky === "always") {
      if (this.belowStayStickyPoint) {
        activateSticky();
      } else {
        disableSticky();
      }
    } else {
      if (this.scrollingUp && this.belowStayStickyPoint) {
        if (!this.stickyActive && this.belowStartStickyPoint) {
          activateSticky();
        }
      } else {
        if (this.stickyActive && this.belowStayStickyPoint) {
          disableSticky();
        } else {
          disableSticky();
        }
      }
    }
  }

  @Listen("scroll", { target: "window" })
  scrollListener() {
    if (this.sticky === "disabled") {
      return;
    }

    readTask(() => {
      this.currentScrollY = window.scrollY;
      this.scrollingUp = this.lastScrollY && this.lastScrollY > this.currentScrollY;
      this.lastScrollY = this.currentScrollY;

      this.belowStayStickyPoint = this.currentScrollY > this.logobarHeight;
      this.belowStartStickyPoint = this.currentScrollY > this.headerHeight;

      this.fixed =
        this.belowStartStickyPoint ||
        (this.scrollingUp && this.belowStayStickyPoint && this.stickyActive) ||
        (this.sticky === "always" && this.belowStayStickyPoint) ||
        this.sticky === "always-full";

      this.headerOverlap = (this.headerTop - this.currentScrollY) / -1;
      this.hideLogo = this.sticky !== "always-full" && this.isMedium && this.headerOverlap > 1;
    });

    writeTask(() => {
      this.updatePosition();
    });
  }

  /** Metanav item click event */
  @Event() stzhMetanavItemClick: EventEmitter<StzhHeaderMetanavItemClickEvent>;

  /** Metanav item child click event */
  @Event() stzhMetanavItemChildClick: EventEmitter<StzhHeaderMetanavItemChildClickEvent>;

  /** Metanav item button click event */
  @Event() stzhMetanavItemButtonClick: EventEmitter<StzhHeaderMetanavItemButtonClickEvent>;

  /** Metanav item click event */
  @Event() stzhMenuItemClick: EventEmitter<StzhHeaderMenuItemClickEvent>;

  /** Language change event */
  @Event() stzhLanguageChange: EventEmitter<StzhHeaderLanguageChangeEvent>;

  /** Search input change event */
  @Event() stzhSearchChange: EventEmitter<StzhHeaderSearchChangeEvent>;

  /** Search input changed event */
  @Event() stzhSearchChanged: EventEmitter<StzhHeaderSearchChangedEvent>;

  @Watch("searchValue")
  searchValueWatcher(newValue: string) {
    this.searchFilled = newValue !== "";
  }

  @Watch("menuItems")
  menuItemsWatcher(newValue: StzhHeaderMenuItem[] | string) {
    if (typeof newValue === "string") {
      this._menuItems = JSON.parse(newValue);
    } else {
      this._menuItems = newValue;
    }

    this._menuItems = this._menuItems.map((menuItem, index) => ({
      id: `submenu-${index}`,
      ...menuItem,
    }));
  }

  @Watch("metanavItems")
  metanavItemsWatcher(newValue: StzhHeaderMetanavItem[] | string) {
    if (typeof newValue === "string") {
      this._metanavItems = JSON.parse(newValue);
    } else {
      this._metanavItems = newValue;
    }

    this._metanavItems = this._metanavItems.map((metanavItem, index) => ({
      id: `metanav-submenu-${index}`,
      ...metanavItem,
    }));
    this.combineMetanavItems();
  }

  @Watch("appNavItems")
  appNavItemsWatcher(newValue: StzhAppNavItem[] | string) {
    if (typeof newValue === "string") {
      this._appNavItems = JSON.parse(newValue);
    } else {
      this._appNavItems = newValue;
    }
  }

  @Watch("userSpecificMetanavItemsNotLoggedIn")
  userSpecificMetanavItemsNotLoggedInWatcher(newValue: StzhHeaderMetanavItem[]) {
    this._userSpecificMetanavItemsNotLoggedIn = newValue;
    this.combineMetanavItems();
  }

  @Watch("languages")
  languagesWatcher(newValue: StzhDropdownOption[] | string) {
    if (typeof newValue === "string") {
      this._languages = JSON.parse(newValue);
    } else {
      this._languages = newValue;
    }
  }

  private renderPromiseResolve: (value?: unknown) => void;

  private currentScrollY: number;
  private lastScrollY: number;
  private headerOverlap: number;
  private scrollingUp: boolean;
  private belowStayStickyPoint: boolean;
  private belowStartStickyPoint: boolean;

  private headerTop: number;
  // private innerHeight: number;
  private mainHeight: number;
  private metabarHeight: number;
  private logobarHeight: number;

  private isMedium: boolean;

  private debounceResize: number;
  private resizeObserver: ResizeObserver;
  private flyoutResizeObserver: ResizeObserver;

  // private innerElement: HTMLDivElement;
  private mainElement: HTMLDivElement;
  private logobarElement: HTMLDivElement;
  private metabarElement: HTMLDivElement;
  private menunavElement: HTMLElement;
  private flyoutElement: HTMLElement;
  private flyoutSearchElement: HTMLElement;

  private searchInput: HTMLInputElement;

  private lastOpener: HTMLButtonElement | null = null;
  private flyoutOpenedByMetanavItem: boolean = null;

  private trap: FocusTrap;
  private _parentElement: HTMLElement;

  private getSiblings() {
    if (!this._parentElement) {
      return [];
    }

    return Array.from(this._parentElement.children).filter(child => child !== this.element);
  }

  private disableSiblings() {
    this.getSiblings().forEach(sibling => {
      sibling.setAttribute("aria-hidden", "true");
    });
  }

  private enableSiblings() {
    this.getSiblings().forEach(sibling => {
      sibling.removeAttribute("aria-hidden");
    });
  }

  private waitForNextRender() {
    return new Promise(resolve => (this.renderPromiseResolve = resolve));
  }

  private resizeMenunavElement() {
    // reset height when metanav is sticky, or on medium or no submenu element is open
    if (media("headerMetanavSticky").matches || media("medium").matches || !this.openSubmenuElement) {
      Object.assign(this.menunavElement.style, {
        height: null,
      });
    } else {
      const { height } = this.openSubmenuElement.getBoundingClientRect();
      Object.assign(this.menunavElement.style, {
        height: `${height}px`,
      });
    }
  }

  private async openMenuItem(event: MouseEvent, menuItem: StzhHeaderMenuItem) {
    this.flyoutOpen = true;

    this.lastOpener = event.currentTarget as HTMLButtonElement;
    this.openSubmenuElement = this.element.querySelector(`#${menuItem.id}`) as HTMLElement;
    this.currentOpenMenuItem = menuItem;
    await this.waitForNextRender();

    requestAnimationFrame(() => {
      this.menunavElement.scrollTop = 0;

      const firstButtonOrLink = this.openSubmenuElement && tabbable(this.openSubmenuElement)[0];
      firstButtonOrLink?.focus();

      this.resizeMenunavElement();
    });
  }

  private async closeMenuItem(focusOpener: boolean = true) {
    this.openSubmenuElement = null;
    this.currentOpenMenuItem = null;
    await this.waitForNextRender();

    if (focusOpener) {
      requestAnimationFrame(() => {
        this.lastOpener.focus();
        this.lastOpener = null;

        this.resizeMenunavElement();
      });
    }
  }

  private async openMetanavItem(event: MouseEvent, metanavItem: StzhHeaderMetanavItem) {
    if (!this.flyoutOpen) {
      this.flyoutOpenedByMetanavItem = true;
      this.flyoutOpen = true;
    }

    this.lastOpener = event.currentTarget as HTMLButtonElement;
    this.currentOpenMetanavItem = metanavItem;
    await this.waitForNextRender();

    requestAnimationFrame(() => {
      const openMetanavSubmenuElement = this.element.querySelector(`#${metanavItem.id}`);
      const firstButtonOrLink = openMetanavSubmenuElement && tabbable(openMetanavSubmenuElement)[0];
      firstButtonOrLink?.focus();
    });
  }

  private async closeMetanavItem() {
    this.currentOpenMetanavItem = null;

    if (this.flyoutOpenedByMetanavItem) {
      this.flyoutOpen = false;
    }

    await this.waitForNextRender();

    requestAnimationFrame(() => {
      this.lastOpener.focus();
      this.lastOpener = null;
    });
  }

  private async openLanguage(event: MouseEvent) {
    if (!this.flyoutOpen) {
      this.flyoutOpenedByMetanavItem = true;
      this.flyoutOpen = true;
    }

    this.lastOpener = event.currentTarget as HTMLButtonElement;
    this.currentOpenLanguage = true;
    await this.waitForNextRender();

    requestAnimationFrame(() => {
      const openMetanavSubmenuElement = this.element.querySelector("#submenu-languages");
      const firstButtonOrLink = openMetanavSubmenuElement && tabbable(openMetanavSubmenuElement)[0];
      firstButtonOrLink?.focus();
    });
  }

  private async closeLanguage() {
    this.currentOpenLanguage = false;

    if (this.flyoutOpenedByMetanavItem) {
      this.flyoutOpen = false;
    }

    await this.waitForNextRender();

    requestAnimationFrame(() => {
      this.lastOpener.focus();
      this.lastOpener = null;
    });
  }

  private handleMenuListFocusout = (event: FocusEvent) => {
    const isNextFocusOutOfMenuList = !(event.currentTarget as HTMLElement).contains(event.relatedTarget as HTMLElement);

    if (this.isMedium && isNextFocusOutOfMenuList) {
      this.closeMenuItem(false);
    }
  };

  private handleBurgerClick = async () => {
    if (this.flyoutOpenedByMetanavItem) {
      this.flyoutOpenedByMetanavItem = false;
      this.closeMetanavItem();
    } else {
      this.flyoutSearchOpen = false;
      this.flyoutOpen = !this.flyoutOpen;
    }

    // focus first menu item
    const element = this.element.querySelector(".stzh-header__menu-item") as HTMLElement;

    if (element) {
      window.setTimeout(() => {
        element.focus();
      }, 100);
    }

    this.updatePosition();
  };

  private handleSearchButtonClick = async () => {
    this.flyoutOpen = false;
    this.flyoutSearchOpen = !this.flyoutSearchOpen;

    // focus first search input
    const element = this.element.querySelector(".stzh-search input[type='search']") as HTMLElement;

    if (element) {
      window.setTimeout(() => {
        element.focus();
      }, 100);
    }
    this.updatePosition();
  };

  private handleBackdropClick = async () => {
    this.flyoutOpen = false;
    this.updatePosition();
  };

  private handleBackdropSearchClick = async () => {
    this.flyoutSearchOpen = false;
    this.updatePosition();
  };

  private handleMetanavItemClick = (event: MouseEvent, item: StzhHeaderMetanavItem) => {
    this.stzhMetanavItemClick.emit({
      component: "stzh-header",
      item,
      originalEvent: event,
    });
  };

  private handleMetanavItemChildClick = (event: MouseEvent, itemChild: StzhHeaderMetanavItemChild) => {
    this.stzhMetanavItemChildClick.emit({
      component: "stzh-header",
      itemChild: itemChild,
      originalEvent: event,
    });
  };

  private handleMetanavItemButtonClick = (event: MouseEvent, itemButton: StzhHeaderMetanavItemButton) => {
    this.stzhMetanavItemButtonClick.emit({
      component: "stzh-header",
      itemButton: itemButton,
      originalEvent: event,
    });
  };

  private handleMenuItemClick = (event: MouseEvent, item: StzhHeaderMenuItem) => {
    this.flyoutOpen = false;
    this.stzhMenuItemClick.emit({
      component: "stzh-header",
      item,
      originalEvent: event,
    });
  };

  private handleSearchInput = (event: InputEvent) => {
    this.searchValue = this.searchInput.value;

    this.stzhSearchChange.emit({
      component: "stzh-header",
      originalEvent: event,
      value: this.searchValue,
    });
  };

  private handleSearchChange = (event: InputEvent) => {
    this.searchValue = this.searchInput.value;
    this.stzhSearchChanged.emit({
      component: "stzh-header",
      originalEvent: event,
      value: this.searchValue,
    });
  };

  private handleLanguageClick = (event: MouseEvent, dropdownOption: StzhDropdownOption) => {
    if (this.languagePreventUrlchange) {
      event.preventDefault();
    }
    this.languageActive = dropdownOption.value;

    this.stzhLanguageChange.emit({
      component: "stzh-header",
      value: this.languageActive,
    });
  };

  private handleFlyoutResize = () => {
    this.resizeMenunavElement();
  };

  private handleResize = () => {
    if (this.sticky === "disabled") {
      return;
    }

    if (this.debounceResize) {
      window.cancelAnimationFrame(this.debounceResize);
    }

    this.debounceResize = requestAnimationFrame(() => {
      readTask(() => {
        this.isMedium = media("medium").matches;

        this.mainHeight = this.mainElement?.offsetHeight || 0;
        this.metabarHeight = this.metabarElement?.offsetHeight || 0;
        this.logobarHeight = this.logobarElement?.offsetHeight || 0;

        this.headerTop = this.element.offsetTop;
        this.headerHeight = this.metabarHeight + this.logobarHeight;
      });

      writeTask(() => {
        document.documentElement.style.setProperty("--stzh-header-height", `${this.headerHeight}px`);
        document.documentElement.style.setProperty("--stzh-header-main-height", `${this.mainHeight}px`);
        document.documentElement.style.setProperty("--stzh-header-metabar-height", `${this.metabarHeight}px`);
        document.documentElement.style.setProperty("--stzh-header-logobar-height", `${this.logobarHeight}px`);
      });

      this.scrollListener();
    });
  };

  /** fetch user specific metanav items from userSpecificMetanavItemsEndpoint */
  @Method()
  async requestUserSpecificMetanavItems() {
    if (!this.userSpecificMetanavItemsEndpoint) {
      return;
    }
    try {
      const response = await fetch(this.userSpecificMetanavItemsEndpoint);
      this.userSpecificMetanavItemsLoggedIn = await response.json();
      this.combineMetanavItems();
    } catch (error) {
      this.combineMetanavItems(true); // fallback to not logged in items
      console.error("Error fetching data:", error);
      if (this.loginErrorMessage) {
        document.querySelector("stzh-toastbar").toast(this.loginErrorMessage, {
          type: "error",
        });
      }
    }
  }

  private combineMetanavItems(fallback: boolean = false) {
    if (!this.userSpecificMetanavItemsEndpoint || fallback) {
      //not logged in or login fetch failed
      this.combinedMetanavItems = [...this._metanavItems, ...this._userSpecificMetanavItemsNotLoggedIn];
    } else {
      this.combinedMetanavItems = [...this._metanavItems, ...this.userSpecificMetanavItemsLoggedIn];
    }
  }

  async componentWillLoad() {
    this.menuItemsWatcher(this.menuItems);
    this.metanavItemsWatcher(this.metanavItems);
    this.appNavItemsWatcher(this.appNavItems);
    this.languagesWatcher(this.languages);
    this.searchValueWatcher(this.searchValue);
    this.userSpecificMetanavItemsNotLoggedInWatcher(this.userSpecificMetanavItemsNotLoggedIn);

    this.requestUserSpecificMetanavItems();

    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "header");
    }
  }

  componentDidRender() {
    if (this.renderPromiseResolve) {
      this.renderPromiseResolve();
    }

    if (this.flyoutElement && this.flyoutResizeObserver) {
      this.flyoutResizeObserver.observe(this.flyoutElement);
    }
    if (this.flyoutSearchElement && this.flyoutResizeObserver) {
      this.flyoutResizeObserver.observe(this.flyoutSearchElement);
    }
  }

  componentDidLoad() {
    this.trap = createFocusTrap(this.element, {
      ...createBaseFocusTrapOptions(),
      initialFocus: false,
    });

    this.flyoutResizeObserver = new ResizeObserver(this.handleFlyoutResize);
    if (this.flyoutElement) {
      this.flyoutResizeObserver.observe(this.flyoutElement);
    }
  }

  connectedCallback() {
    this._parentElement = this.element.parentElement;

    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(this.element);
  }

  disconnectedCallback() {
    this.flyoutOpenWatcher(false);
    this.resizeObserver?.disconnect();
    this.flyoutResizeObserver?.disconnect();
  }

  render() {
    const metanavBeforeUsed: boolean = hasSlot(this.element, "metanav-before");
    const metanavAfterUsed: boolean = hasSlot(this.element, "metanav-after");

    const classes = {
      "stzh-header": true,
      "stzh-header--is-flyout-open": this.flyoutOpen,
      "stzh-header--is-flyout-search-open": this.flyoutSearchOpen,
      "stzh-header--is-search-filled": this.searchFilled,
      "stzh-header--is-fixed": this.fixed,
      "stzh-header--is-fixed-transition": this.fixedTransition,
      "stzh-header--is-sticky": this.stickyActive,
      "stzh-header--is-sticky-transition": this.stickyActive && this.fixedTransition,
      "stzh-header--hide-logo": this.hideLogo,
      "stzh-header--has-empty-metabar-mobile":
        !metanavBeforeUsed &&
        !metanavAfterUsed &&
        this._metanavItems.filter(item => item.stay || item.stayAndShowInMobileMenu).length === 0 &&
        this._menuItems.length === 0 &&
        (this._languages.length === 0 || (this._languages.length > 0 && !this.languageStay)) &&
        !this.searchAction,
      "stzh-header--has-empty-metabar":
        !metanavBeforeUsed &&
        !metanavAfterUsed &&
        this._metanavItems.length === 0 &&
        this._menuItems.length === 0 &&
        this._languages.length === 0 &&
        !this.searchAction,
      [`stzh-header--sticky-${this.sticky}`]: !!this.sticky,
    };

    const activeLanguage = this.languageActive || window.stzhComponents.utils.getLocale(this.element);
    const activeLanguageOption =
      this._languages?.find(({ value }) => value === activeLanguage) || this._languages?.[0] || null;

    const renderMetanavItemButton = (
      item: StzhHeaderMetanavItem,
      additionalClasses?: { [className: string]: boolean },
      additionalAttributes?: any
    ) => {
      const Element = item.items?.length > 0 || item.itemButton || !item.href ? "button" : "a";

      return (
        <Element
          {...additionalAttributes}
          href={Element === "a" && item.href}
          target={Element === "a" && item.target}
          style={{ order: item.cssOrder }}
          class={{
            "stzh-header__metanav-item": true,
            "is-heavy": item.important,
            "is-stay": item.stay || item.stayAndShowInMobileMenu,
            ...(additionalClasses || {}),
          }}
        >
          <span
            class={{
              "stzh-header__metanav-item-text": true,
              "is-vhidden": item.labelHidden,
              "has-no-short-label": !item.labelShort,
            }}
          >
            <span class="stzh-header__metanav-item-text-label-long">{item.label}</span>
            <span class="stzh-header__metanav-item-text-label-short" aria-hidden="true">
              {item.labelShort}
            </span>
          </span>
          {item.icon && (
            <span class="stzh-header__metanav-icon-wrapper">
              {item.icon && (
                <stzh-icon
                  class={`stzh-header__metanav-icon ${item.iconOpen ? "is-close" : ""}`}
                  name={item.icon}
                ></stzh-icon>
              )}
              {item.iconOpen && <stzh-icon class="stzh-header__metanav-icon is-open" name={item.iconOpen}></stzh-icon>}
              {(item.badge || item.badgeEmpty) && (
                <stzh-badge
                  class="stzh-header__metanav-icon-badge"
                  label={item.badge}
                  type={typeof item.badgeType === "undefined" ? "error" : item.badgeType}
                ></stzh-badge>
              )}
            </span>
          )}
        </Element>
      );
    };

    return (
      <Host>
        <header class={classes}>
          <div class="stzh-header__header" style={{ paddingTop: `${this.paddingTop}px` }}>
            {this._appNavItems.length > 0 && (
              <div class="stzh-header__app-nav-bottom">
                <stzh-app-nav items={this._appNavItems}></stzh-app-nav>
              </div>
            )}
            <div
              class="stzh-header__inner"
              // ref={(el) => (this.innerElement = el as HTMLDivElement)}
            >
              <div class="stzh-header__main" ref={el => (this.mainElement = el as HTMLDivElement)}>
                <div class="stzh-header__logobar" ref={el => (this.logobarElement = el as HTMLDivElement)}>
                  <div class="stzh-header__logobar-logo">
                    <a
                      href={this.href}
                      class="stzh-header__logo-link"
                      s-object-id={this.logoAnalyticsId || "Header Logo"}
                    >
                      <slot name="logo"></slot>
                    </a>
                  </div>
                  <div class="stzh-header__logobar-decoration"></div>
                </div>

                {!this.hideMetabar && (
                  <div class="stzh-header__metabar" ref={el => (this.metabarElement = el as HTMLDivElement)}>
                    <div class="stzh-header__metabar-inner">
                      <slot name="menu-before"></slot>

                      {this._menuItems.length > 0 && (
                        <button
                          id={this.menuId}
                          class={`stzh-header__burger ${
                            this.flyoutOpen && !this.flyoutOpenedByMetanavItem && "is-open"
                          }`}
                          onClick={this.handleBurgerClick}
                        >
                          <stzh-icon class="stzh-header__burger-icon is-close" name="menu"></stzh-icon>
                          <stzh-icon class="stzh-header__burger-icon is-open" name="close"></stzh-icon>
                          <div class="stzh-header__burger-text">{this.localization.menuLabel}</div>
                        </button>
                      )}
                      {this.searchAction && !hasSlot(this.element, "search") && (
                        <form class="stzh-header__metabar-search" action={this.searchAction} role="search">
                          <label class="stzh-header__search">
                            <input
                              id={this.searchId}
                              ref={el => (this.searchInput = el as HTMLInputElement)}
                              class="stzh-header__search-input"
                              type="search"
                              name={this.searchFieldName}
                              onChange={this.handleSearchChange}
                              onInput={this.handleSearchInput}
                            ></input>
                            <stzh-icon class="stzh-header__search-icon" name="search"></stzh-icon>
                            <div class="stzh-header__search-text">{this.localization.searchLabel}</div>
                          </label>
                        </form>
                      )}
                      {hasSlot(this.element, "search") && (
                        <stzh-button
                          iconPosition={"left"}
                          onClick={this.handleSearchButtonClick}
                          variant="tertiary-plain"
                          size="small"
                        >
                          <stzh-icon
                            slot="icon"
                            name="search"
                            class={this.flyoutSearchOpen ? "stzh-header__search-button--is-hidden" : ""}
                          ></stzh-icon>
                          <stzh-icon
                            slot="icon"
                            name="close"
                            class={this.flyoutSearchOpen ? "" : "stzh-header__search-button--is-hidden"}
                          ></stzh-icon>
                          {this.localization.searchLabel}
                        </stzh-button>
                      )}

                      <slot name="menu-after"></slot>

                      {this._appNavItems.length > 0 && (
                        <div class="stzh-header__app-nav">
                          <stzh-app-nav items={this._appNavItems}></stzh-app-nav>
                        </div>
                      )}

                      <div class="stzh-header__metabar-nav" id={this.metanavId}>
                        <slot name="metanav-before"></slot>
                        {this.combinedMetanavItems.map(item =>
                          item.items?.length > 0 || item.itemButton ? (
                            <Fragment>
                              {renderMetanavItemButton(
                                item,
                                {
                                  "is-popover-mobile": true,
                                },
                                {
                                  "aria-expanded": item === this.currentOpenMetanavItem ? "true" : "false",
                                  "aria-controls": item.id,
                                  onClick: (e: MouseEvent) => {
                                    if (item === this.currentOpenMetanavItem) {
                                      this.closeMetanavItem();
                                    } else {
                                      this.openMetanavItem(e, item);
                                    }

                                    this.handleMetanavItemClick(e, item);
                                  },
                                }
                              )}
                              <stzh-popover
                                class={{
                                  "stzh-header__metanav-popover": true,
                                }}
                                placement="bottom-end"
                                distance={20}
                                variant={item.variant}
                                size={typeof item.size === "undefined" ? "large" : item.size}
                              >
                                {renderMetanavItemButton(
                                  item,
                                  {
                                    "is-popover-desktop": true,
                                  },
                                  {
                                    onClick: (e: MouseEvent) => {
                                      this.handleMetanavItemClick(e, item);
                                    },
                                  }
                                )}
                                <div slot="content">
                                  {item.items?.length > 0 && (
                                    <stzh-menu>
                                      {item.items.map(childItem => (
                                        <stzh-menu-item
                                          class={{
                                            "stzh-header__metanav-menu-item": true,
                                            "is-heavy":
                                              typeof item.importantSubmenu === "undefined" || item.importantSubmenu,
                                          }}
                                          size={typeof item.size === "undefined" ? "large" : item.size}
                                          variant={item.variant}
                                          href={childItem.href}
                                          target={childItem.target}
                                          icon={childItem.icon}
                                          counter={childItem.counter}
                                          badge={childItem.badge}
                                          badgeType={childItem.badgeType}
                                          onClick={(e: MouseEvent) => {
                                            this.handleMetanavItemChildClick(e, childItem);
                                          }}
                                        >
                                          {childItem.label}
                                        </stzh-menu-item>
                                      ))}
                                    </stzh-menu>
                                  )}
                                </div>
                                {item.itemButton && (
                                  <stzh-button
                                    slot="action"
                                    size={item.itemButton.size}
                                    href={item.itemButton.href}
                                    target={item.itemButton.target}
                                    onClick={(e: MouseEvent) => {
                                      this.handleMetanavItemButtonClick(e, item.itemButton);
                                    }}
                                  >
                                    {item.itemButton.label}
                                  </stzh-button>
                                )}
                              </stzh-popover>
                            </Fragment>
                          ) : (
                            renderMetanavItemButton(
                              item,
                              {},
                              {
                                onClick: (e: MouseEvent) => {
                                  this.handleMetanavItemClick(e, item);
                                },
                              }
                            )
                          )
                        )}

                        {this._languages?.length > 0 && (
                          <Fragment>
                            <h2 id={this.languageId} class="stzh-header__vhidden">
                              {this.localization.languageLabel}
                            </h2>
                            {this._menuItems.length > 0 && this.languageStay && (
                              <button
                                class={{
                                  "stzh-header__metanav-item": true,
                                  "is-popover-mobile": true,
                                  "is-stay": this.languageStay,
                                }}
                                aria-expanded={this.currentOpenLanguage ? "true" : "false"}
                                aria-controls="submenu-languages"
                                onClick={(e: MouseEvent) => {
                                  if (this.currentOpenLanguage) {
                                    this.closeLanguage();
                                  } else {
                                    this.openLanguage(e);
                                  }
                                }}
                              >
                                <span class="stzh-header__metanav-item-text">{activeLanguageOption?.text}</span>
                                <stzh-icon class="stzh-header__metanav-icon is-close" name="angle-down"></stzh-icon>
                                <stzh-icon class="stzh-header__metanav-icon is-open" name="angle-up"></stzh-icon>
                              </button>
                            )}
                            <stzh-popover
                              class="stzh-header__metanav-popover is-langnav"
                              placement="bottom-end"
                              distance={20}
                            >
                              <button
                                class={{
                                  "stzh-header__metanav-item": true,
                                  "is-popover-desktop": this._menuItems.length > 0,
                                  "is-stay": this.languageStay,
                                }}
                              >
                                <span class="stzh-header__metanav-item-text">{activeLanguageOption?.text}</span>
                                <stzh-icon class="stzh-header__metanav-icon is-close" name="angle-down"></stzh-icon>
                                <stzh-icon class="stzh-header__metanav-icon is-open" name="angle-up"></stzh-icon>
                              </button>
                              <div slot="content">
                                <stzh-menu>
                                  {this._languages.map(language => (
                                    <stzh-menu-item
                                      class="stzh-header__metanav-menu-item"
                                      active={activeLanguageOption?.value === language.value}
                                      href={language.value}
                                      onClick={event => this.handleLanguageClick(event, language)}
                                    >
                                      {language.text}
                                    </stzh-menu-item>
                                  ))}
                                </stzh-menu>
                              </div>
                            </stzh-popover>
                          </Fragment>
                        )}

                        <slot name="metanav-after"></slot>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {(this._menuItems.length > 0 || this.combinedMetanavItems?.length > 0) && (
            <div ref={el => (this.flyoutElement = el as HTMLElement)} class="stzh-header__flyout">
              <div class="stzh-header__flyout-backdrop" onClick={this.handleBackdropClick}></div>
              <div class="stzh-header__flyout-scrollbar"></div>
              <div class="stzh-header__menu">
                <nav
                  class="stzh-header__menu-nav"
                  ref={el => (this.menunavElement = el as HTMLElement)}
                  aria-label={this.localization.navigationLabel}
                >
                  <ul class="stzh-header__menu-list is-level-1">
                    {this._menuItems.map(item => (
                      <li
                        class={{
                          "stzh-header__menu-list-item": true,
                          "is-open": item === this.currentOpenMenuItem,
                        }}
                      >
                        {item.items?.length > 0 ? (
                          <button
                            aria-expanded={item === this.currentOpenMenuItem ? "true" : "false"}
                            aria-controls={item.id}
                            class="stzh-header__menu-item is-level-1 has-items"
                            onClick={
                              item === this.currentOpenMenuItem
                                ? () => this.closeMenuItem()
                                : e => this.openMenuItem(e, item)
                            }
                          >
                            {item.label}
                          </button>
                        ) : (
                          <a
                            href={item.href}
                            onClick={(e: MouseEvent) => this.handleMenuItemClick(e, item)}
                            class="stzh-header__menu-item is-level-1"
                          >
                            {item.label}
                          </a>
                        )}
                        {item.items?.length > 0 && (
                          <ul
                            id={item.id}
                            class="stzh-header__menu-list is-level-2"
                            onFocusout={this.handleMenuListFocusout}
                          >
                            <li class="stzh-header__menu-list-item is-backlink">
                              <button
                                class="stzh-header__menu-item is-level-2 is-backlink"
                                onClick={() => this.closeMenuItem()}
                              >
                                <stzh-icon name="angle-left"></stzh-icon>
                                <span>{this.menuBackLabel ? this.menuBackLabel : this.localization.menuBackLabel}</span>
                              </button>
                            </li>
                            <li class="stzh-header__menu-list-item">
                              <a
                                href={item.href}
                                onClick={(e: MouseEvent) => this.handleMenuItemClick(e, item)}
                                class="stzh-header__menu-item is-level-2 is-main"
                              >
                                {item.label}
                              </a>
                            </li>
                            {item.items.map(item => (
                              <li class="stzh-header__menu-list-item">
                                <a
                                  href={item.href}
                                  onClick={(e: MouseEvent) => this.handleMenuItemClick(e, item)}
                                  class="stzh-header__menu-item is-level-2"
                                >
                                  {item.label}
                                </a>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                </nav>

                {(this.combinedMetanavItems?.length > 0 || this._languages?.length > 0) && (
                  <div class="stzh-header__menu-metanav">
                    <nav class="stzh-header__menu-metanav-nav">
                      <ul class="stzh-header__menu-metanav-list is-level-1">
                        {this.combinedMetanavItems.map(item => (
                          <li
                            class={{
                              "stzh-header__menu-metanav-list-item": true,
                              "is-open": item === this.currentOpenMetanavItem,
                            }}
                          >
                            {item.items?.length > 0 ? (
                              <button
                                aria-expanded={item === this.currentOpenMetanavItem ? "true" : "false"}
                                aria-controls={item.id}
                                class={{
                                  "stzh-header__menu-metanav-item is-level-1": true,
                                  "is-hidden": item.stay && !item.stayAndShowInMobileMenu,
                                }}
                                onClick={(e: MouseEvent) => {
                                  if (item === this.currentOpenMetanavItem) {
                                    this.closeMetanavItem();
                                  } else {
                                    this.openMetanavItem(e, item);
                                  }

                                  this.handleMetanavItemClick(e, item);
                                }}
                              >
                                <span
                                  class={{
                                    "stzh-header__menu-metanav-item-text": true,
                                    "is-vhidden": item.labelHidden,
                                  }}
                                >
                                  {item.label}
                                </span>
                                {item.icon && (
                                  <stzh-icon class="stzh-header__menu-metanav-item-icon" name={item.icon}></stzh-icon>
                                )}
                              </button>
                            ) : (
                              <a
                                href={item.href}
                                class={{
                                  "stzh-header__menu-metanav-item is-level-1": true,
                                  "is-hidden": item.stay && !item.stayAndShowInMobileMenu,
                                }}
                                onClick={(e: MouseEvent) => {
                                  this.handleMetanavItemClick(e, item);
                                }}
                              >
                                <span
                                  class={{
                                    "stzh-header__menu-metanav-item-text": true,
                                    "is-vhidden": item.labelHidden,
                                  }}
                                >
                                  {item.label}
                                </span>
                                {item.icon && (
                                  <stzh-icon class="stzh-header__menu-metanav-item-icon" name={item.icon}></stzh-icon>
                                )}
                              </a>
                            )}
                            {item.items?.length > 0 && (
                              <ul id={item.id} class="stzh-header__menu-metanav-list is-level-2">
                                <li class="stzh-header__menu-metanav-list-item">
                                  <div class="stzh-header__menu-metanav-item is-level-2 is-title">
                                    <span>{item.label}</span>
                                    <stzh-button
                                      class="stzh-header__menu-metanav-item-icon"
                                      variant="tertiary"
                                      icon="close"
                                      iconOnly={true}
                                      label={this.localization.closeMetanavMenuLabel?.replace(
                                        /\{itemLabel\}/gi,
                                        item.label
                                      )}
                                      onClick={() => this.closeMetanavItem()}
                                    ></stzh-button>
                                  </div>
                                </li>
                                {item.items.map(item => (
                                  <li class="stzh-header__menu-metanav-list-item">
                                    <a href={item.href} class="stzh-header__menu-metanav-item is-level-2">
                                      {item.icon && (
                                        <stzh-icon
                                          class="stzh-header__menu-metanav-item-icon"
                                          name={item.icon}
                                        ></stzh-icon>
                                      )}
                                      <span class="stzh-header__menu-metanav-item-text">
                                        <span>{item.label}</span>
                                        {item.counter !== undefined && item.counter > 0 && (
                                          <span class="stzh-header__menu-metanav-item-counter">({item.counter})</span>
                                        )}
                                      </span>
                                    </a>
                                  </li>
                                ))}

                                {item.itemButton && (
                                  <li class="stzh-header__menu-metanav-list-item">
                                    <div class="stzh-header__menu-metanav-item is-level-2 is-action">
                                      <stzh-button href={item.itemButton.href} target={item.itemButton.target}>
                                        {item.itemButton.label}
                                      </stzh-button>
                                    </div>
                                  </li>
                                )}
                              </ul>
                            )}
                          </li>
                        ))}

                        {this._languages?.length > 0 && (
                          <li
                            class={{
                              "stzh-header__menu-metanav-list-item": true,
                              "is-open": this.currentOpenLanguage,
                            }}
                          >
                            <button
                              aria-expanded={this.currentOpenLanguage ? "true" : "false"}
                              aria-controls="submenu-languages"
                              class="stzh-header__menu-metanav-item is-level-1"
                              onClick={e => this.openLanguage(e)}
                            >
                              <span>{activeLanguageOption?.text}</span>
                              <stzh-icon class="stzh-header__menu-metanav-item-icon" name="angle-down"></stzh-icon>
                            </button>
                            <ul id="submenu-languages" class="stzh-header__menu-metanav-list is-level-2">
                              <div class="stzh-header__menu-metanav-item is-level-2 is-title">
                                <span>{this.localization.dialogLanguageTitle}</span>
                                <stzh-button
                                  class="stzh-header__menu-metanav-item-icon"
                                  variant="tertiary"
                                  icon="close"
                                  iconOnly={true}
                                  label={"Sprach-Dialog schliessen"}
                                  onClick={() => this.closeLanguage()}
                                ></stzh-button>
                              </div>
                              {this._languages.map(language => (
                                <li class="stzh-header__menu-metanav-list-item">
                                  <a
                                    class="stzh-header__menu-metanav-item is-level-2 is-language"
                                    href={language.value}
                                    onClick={event => this.handleLanguageClick(event, language)}
                                  >
                                    <span>{language.text}</span>
                                    {activeLanguageOption?.value === language.value && (
                                      <stzh-icon
                                        class="stzh-header__menu-metanav-item-icon"
                                        name="checkmark"
                                      ></stzh-icon>
                                    )}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </li>
                        )}
                      </ul>
                    </nav>
                  </div>
                )}
              </div>
            </div>
          )}

          {hasSlot(this.element, "search") && (
            <div ref={el => (this.flyoutSearchElement = el as HTMLElement)} class="stzh-header__flyout-search">
              <div class="stzh-header__flyout-backdrop" onClick={this.handleBackdropSearchClick}></div>
              <div class="stzh-header__flyout-scrollbar"></div>
              <div class="stzh-header__flyout-search-main">
                <slot name="search"></slot>
              </div>
            </div>
          )}
        </header>
      </Host>
    );
  }
}
